import { API_URL } from '../env';

/**
 * Shape returned by the Solve geo endpoint (`GET {API_URL}/v1/geo`). The server
 * derives these from the request IP. See the endpoint spec in the PR / handoff
 * doc. All fields optional so a partial/unknown result never throws here.
 */
export interface GeoLocationResponse {
  /** ISO 3166-1 alpha-2 country code (Puerto Rico is its own code, `PR`). */
  country?: string;
  /** ISO 3166-2 subdivision, e.g. `PR` or `CA`, when `country` is `US`. */
  region?: string;
  /** Echoed request IP — for debugging only; do not rely on it. */
  ip?: string;
}

/**
 * Best-effort IP geolocation. Used to seed the US/PR `location` split before an
 * address is known. Never throws and never blocks the app: any failure (offline,
 * non-2xx, aborted, malformed body) resolves to null and the caller keeps the
 * current best-guess location.
 *
 * Unauthenticated on purpose — location must be resolvable pre-login during
 * onboarding. The endpoint reads the request IP, so it works identically for
 * web and native (native hits the Solve API directly).
 */
export const fetchGeoLocation = async (signal?: AbortSignal): Promise<GeoLocationResponse | null> => {
  try {
    const response = await fetch(`${API_URL}/v1/geo`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) return null;
    return (await response.json()) as GeoLocationResponse;
  } catch {
    // Swallow all errors — geolocation is advisory, not required.
    return null;
  }
};
